import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Landing from './pages/landing/Landing';
import ReservationDetails from './pages/reservationDetails/ReservationDetails';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { oktaAuthConfig } from './utils/config';
import styles from './App.module.css';
import Spinner from './components/UI/spinner/Spinner';
import { readBookingAdminToolSettings } from './apiCall';
import CrisisToolStatusContext from './context/CrisisToolStatusContext';

const oktaAuth = new OktaAuth(oktaAuthConfig);

function App() {
  const navigate = useNavigate();
  const [isCrisisToolActive, setIsCrisisToolActive] = useState<boolean | null>(null);
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), { replace: true });
  };

  const revalidateCrisisToolStatus = useCallback(async () => {
    try {
      const res = await readBookingAdminToolSettings();
      const isActive =
        typeof res !== 'string' && Boolean(res.settings?.enableAdvanceFunctionallity);
      setIsCrisisToolActive(isActive);
      return isActive;
    } catch {
      setIsCrisisToolActive(false);
      return false;
    }
  }, []);

  useEffect(() => {
    void revalidateCrisisToolStatus();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void revalidateCrisisToolStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [revalidateCrisisToolStatus]);

  const markCrisisToolDeactivated = useCallback(() => setIsCrisisToolActive(false), []);

  const crisisToolStatus = useMemo(
    () => ({
      isCrisisToolActive: isCrisisToolActive === true,
      revalidateCrisisToolStatus,
      markCrisisToolDeactivated
    }),
    [isCrisisToolActive, revalidateCrisisToolStatus, markCrisisToolDeactivated]
  );

  if (isCrisisToolActive === null) {
    return (
      <div className={styles.appContainer} data-testid="adminSettingsLoadingTestId">
        <Spinner />
      </div>
    );
  }

  if (!isCrisisToolActive) {
    return (
      <div className={styles.appContainer} data-testid="adminSettingsDisabledTestId">
        <p className={styles.deactivatedMessage}>The Crisis Tool is currently deactivated.</p>
      </div>
    );
  }

  return (
    <div className={styles.appContainer}>
      <CrisisToolStatusContext.Provider value={crisisToolStatus}>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/reservation/:reservationId/" element={<ReservationDetails />} />
            <Route path="/login/callback" element={<LoginCallback />} />
            <Route path="*" element={<Landing />} />
          </Routes>
          <Spinner />
        </Security>
      </CrisisToolStatusContext.Provider>
    </div>
  );
}

export default App;
